import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import dictsService, { DictItem } from '../services/dicts.service';
import stageService from '../services/stage.service';
import type { Stage } from '../types/stage.types';

export interface TicketDictsState {
  statuses: DictItem[];
  types: DictItem[];
  priorities: DictItem[];
  slas: DictItem[];
  labels: DictItem[];
  stages: Stage[];
}

interface DictsContextValue {
  dicts: TicketDictsState;
  loading: boolean;
  loaded: boolean;
  ensureLoaded: () => Promise<void>;
  setDicts: (patch: Partial<TicketDictsState>) => void;
  invalidateAll: () => void;
}

const defaultState: TicketDictsState = {
  statuses: [],
  types: [],
  priorities: [],
  slas: [],
  labels: [],
  stages: [],
};

const DictsContext = createContext<DictsContextValue | undefined>(undefined);

export const useDicts = (): DictsContextValue => {
  const ctx = useContext(DictsContext);
  if (!ctx) throw new Error('useDicts must be used within DictsProvider');
  return ctx;
};

export const DictsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [dicts, setDictsState] = useState<TicketDictsState>(defaultState);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  const setDicts = useCallback((patch: Partial<TicketDictsState>) => {
    setDictsState((prev) => ({ ...prev, ...patch }));
  }, []);

  // Ref для проверки состояния внутри async-функции (избегаем stale closure)
  const loadingRef = useRef(false);
  const loadedRef = useRef(false);

  const ensureLoaded = useCallback(async () => {
    if (loadedRef.current || loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const [statuses, types, priorities, slas, labels, stages] = await Promise.all([
        dictsService.listDictItems('ticket_statuses'),
        dictsService.listDictItems('ticket_types'),
        dictsService.listDictItems('ticket_priorities'),
        dictsService.listDictItems('ticket_slas'),
        dictsService.listDictItems('ticket_labels'),
        // этапы из нового API
        stageService.list({ service_types: ['tickets'] }).catch(() => []),
      ]);
      setDictsState({ statuses, types, priorities, slas, labels, stages });
      loadedRef.current = true;
      setLoaded(true);
    } catch (e) {
      // Не бросаем — позволим UI деградировать до ID/—
      console.error('Dicts ensureLoaded error', e);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  const invalidateAll = useCallback(() => {
    loadedRef.current = false;
    setLoaded(false);
  }, []);

  const value = useMemo<DictsContextValue>(
    () => ({ dicts, loading, loaded, ensureLoaded, setDicts, invalidateAll }),
    [dicts, loading, loaded, ensureLoaded, setDicts, invalidateAll]
  );

  return <DictsContext.Provider value={value}>{children}</DictsContext.Provider>;
};
